/**
 * Argentina Province Analytics Integration
 * Enhanced tracking for Google Analytics con provincia derivada de la VERDAD
 * ÚNICA de location: el Worker (__TEMPLATE_CONFIG__ vía serverTemplateDetection).
 */

import { getArgentinaProvinceCodeByName, getArgentinaProvinceConfig, type ArgentinaProvinceConfig } from "@/config/argentinaProvinces";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
// Amplitude tracking removed

// Google Analytics gtag function (global)
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Enhanced province detection with fallback methods
 */
export function detectArgentinaProvinceEnhanced(): {
  provinceCode: string | null;
  provinceName: string | null;
  region: string | null;
  economicLevel: string | null;
  detectionMethod: 'worker' | 'test' | 'fallback' | 'none';
  confidence: 'high' | 'medium' | 'low';
} {
  if (typeof window === "undefined") {
    return {
      provinceCode: null,
      provinceName: null,
      region: null,
      economicLevel: null,
      detectionMethod: 'none',
      confidence: 'low'
    };
  }

  // Check for test province first (development)
  const testProvince = localStorage.getItem("test_argentina_province");
  if (testProvince && import.meta.env.DEV) {
    const config = getArgentinaProvinceConfig(testProvince);
    return {
      provinceCode: testProvince,
      provinceName: config?.provinceName || null,
      region: config?.region || null,
      economicLevel: config?.economicLevel || null,
      detectionMethod: 'test',
      confidence: 'high'
    };
  }

  // VERDAD ÚNICA: provincia del Worker (__TEMPLATE_CONFIG__). Nada de
  // timezone: America/Argentina/Buenos_Aires cubre CABA y media Argentina,
  // así que la detección anterior etiquetaba casi todo como "BA".
  const cfg = getServerTemplateConfig();
  if (cfg?.country === "AR") {
    const code = getArgentinaProvinceCodeByName(cfg.province);
    if (code) {
      const config = getArgentinaProvinceConfig(code);
      return {
        provinceCode: code,
        provinceName: config?.provinceName || cfg.province || null,
        region: config?.region || null,
        economicLevel: config?.economicLevel || null,
        detectionMethod: 'worker',
        confidence: 'high'
      };
    }
    return {
      provinceCode: "AR_UNKNOWN",
      provinceName: "Argentina (Provincia no detectada)",
      region: "UNKNOWN",
      economicLevel: "unknown",
      detectionMethod: 'fallback',
      confidence: 'medium'
    };
  }

  // No Argentina detected
  return {
    provinceCode: null,
    provinceName: null,
    region: null,
    economicLevel: null,
    detectionMethod: 'none',
    confidence: 'low'
  };
}

/**
 * Track province detection event in Amplitude
 */
export function trackProvinceDetection(): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed
  }
}

/**
 * Track template selection based on province
 */
export function trackProvinceTemplateSelection(templateName: string): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed

    // Also track in Google Analytics if available
    if (window.gtag) {
      window.gtag('event', 'argentina_template_selected', {
        custom_parameter_1: detection.provinceCode,
        custom_parameter_2: templateName,
        custom_parameter_3: detection.economicLevel,
        custom_parameter_4: detection.region
      });
    }
  }
}

/**
 * Track user interactions by province
 */
export function trackProvinceInteraction(interactionType: string, details: Record<string, unknown> = {}): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed
  }
}

/**
 * Track conversions by province (purchases, form submissions, etc.)
 */
export function trackProvinceConversion(conversionType: string, value?: number, currency?: string): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    const eventData: Record<string, unknown> = {
      conversion_type: conversionType,
      province_code: detection.provinceCode,
      province_name: detection.provinceName,
      province_region: detection.region,
      economic_level: detection.economicLevel,
      timestamp: new Date().toISOString()
    };

    if (value !== undefined) {
      eventData.conversion_value = value;
    }
    if (currency) {
      eventData.currency = currency;
    }

    // Amplitude tracking removed

    // Enhanced Google Analytics conversion tracking
    if (window.gtag && value !== undefined) {
      window.gtag('event', 'purchase', {
        transaction_id: `ar_${detection.provinceCode}_${Date.now()}`,
        value: value,
        currency: currency || 'ARS',
        custom_parameter_1: detection.provinceCode,
        custom_parameter_2: detection.economicLevel,
        custom_parameter_3: detection.region
      });
    }
  }
}

/**
 * Track page views with province data
 */
export function trackProvincePageView(pagePath: string, pageTitle?: string): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed

    // Enhanced Google Analytics page view
    if (window.gtag) {
      window.gtag('config', (import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GA_TRACKING_ID || 'G-D1JQVWYJM6'), {
        page_title: pageTitle,
        page_location: window.location.href,
        custom_parameter_1: detection.provinceCode,
        custom_parameter_2: detection.economicLevel,
        custom_parameter_3: detection.region
      });
    }
  }
}

/**
 * Track template performance metrics by province
 */
export function trackProvinceTemplatePerformance(metrics: {
  templateName: string;
  loadTime?: number;
  interactionTime?: number;
  scrollDepth?: number;
  ctaClicks?: number;
}): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed
  }
}

/**
 * Get analytics data for province dashboard
 */
export function getProvinceAnalyticsData(): {
  currentProvince: ArgentinaProvinceConfig | null;
  detection: ReturnType<typeof detectArgentinaProvinceEnhanced>;
  sessionData: {
    sessionStart: string;
    pageViews: number;
    interactions: number;
  };
} {
  const detection = detectArgentinaProvinceEnhanced();
  const currentProvince = detection.provinceCode ? getArgentinaProvinceConfig(detection.provinceCode) : null;
  
  // Get session data from sessionStorage
  const sessionData = {
    sessionStart: sessionStorage.getItem('argentina_session_start') || new Date().toISOString(),
    pageViews: parseInt(sessionStorage.getItem('argentina_page_views') || '0'),
    interactions: parseInt(sessionStorage.getItem('argentina_interactions') || '0')
  };

  // Initialize session if needed
  if (!sessionStorage.getItem('argentina_session_start')) {
    sessionStorage.setItem('argentina_session_start', sessionData.sessionStart);
  }

  return {
    currentProvince,
    detection,
    sessionData
  };
}

/**
 * Initialize province analytics tracking
 */
export function initProvinceAnalytics(): void {
  // Track initial province detection
  trackProvinceDetection();
  
  // Set up session tracking
  const sessionData = getProvinceAnalyticsData().sessionData;
  sessionStorage.setItem('argentina_session_start', sessionData.sessionStart);
  
  // Track initial page view
  trackProvincePageView(window.location.pathname, document.title);
  
  // Increment page view counter
  const currentPageViews = parseInt(sessionStorage.getItem('argentina_page_views') || '0');
  sessionStorage.setItem('argentina_page_views', (currentPageViews + 1).toString());
  
  if (import.meta.env.DEV) {
  }
}

/**
 * Batch tracking for multiple events (performance optimization)
 */
export function batchTrackProvinceEvents(events: Array<{
  type: 'interaction' | 'conversion' | 'performance';
  data: Record<string, unknown>;
}>): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode && events.length > 0) {
    const batchData = {
      province_code: detection.provinceCode,
      province_name: detection.provinceName,
      province_region: detection.region,
      economic_level: detection.economicLevel,
      batch_size: events.length,
      events: events,
      timestamp: new Date().toISOString()
    };

    // Amplitude tracking removed
  }
}

// Export all functions for easy use
export default {
  detectArgentinaProvinceEnhanced,
  trackProvinceDetection,
  trackProvinceTemplateSelection,
  trackProvinceInteraction,
  trackProvinceConversion,
  trackProvincePageView,
  trackProvinceTemplatePerformance,
  getProvinceAnalyticsData,
  initProvinceAnalytics,
  batchTrackProvinceEvents
};